import BaseAccordion from 'components/Accordions/BaseAccordion';
import { EllipsisMenu } from 'components/index';
import { usePerms } from 'contexts/PermsContext';
import { deviceSearchFilter } from 'features/integrations/utils/integrationSearchUtils';
import { useMemo } from 'react';
import { Button } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { getDynamicDeviceIcon } from 'utils/iconUtils';
import {
  getfilteredConnectedDevices,
  getFilteredDeviceTypes,
} from '../utils/connectedSystemsUtils';
import { hasDevicePermissionFromDevice } from '../utils/devicePermissionUtils';

import {
  selectDeviceTypeMap,
  selectIntegrations,
  selectSystemCount,
} from 'features/integrations/state/selectors/integrationSelectors';
import { useGetConnectedDevicesQuery } from 'services/devices/devices.api';
import { useFusionGetIntegrationsQuery } from 'services/integrations/integrations.api';

const Accordion = ({
  deviceIndexMap,
  filteredIntegrations,
  openEditFormForSystemOrDevice,
  prepareSystemOrDeviceForDeletion,
  deviceRowIdEllipsisOpen,
  setDeviceRowIdEllipsisOpen,
  systemRowIdEllipsisOpen,
  setSystemRowIdEllipsisOpen,
}) => {
  const perms = usePerms();

  // ✅ explicit selectors (no selectDeviceData)
  const {
    data: connectedDevices = [],
    isLoading: connectedDevicesLoading,
    isError: connectedDevicesError,
  } = useGetConnectedDevicesQuery();
  const deviceTypeMap = useSelector(selectDeviceTypeMap);
  const { data: integrations = [] } = useFusionGetIntegrationsQuery();
  const systemCount = useSelector(selectSystemCount);

  // ✅ compatibility object ONLY for helpers still expecting the old deviceData shape
  const deviceDataCompat = useMemo(
    () => ({
      connectedDevices,
      deviceTypeMap,
      integrations,
      systemCount,
    }),
    [connectedDevices, deviceTypeMap, integrations, systemCount],
  );

  return (
    <>
      {filteredIntegrations?.map((system, index) => {
        const filteredDeviceTypes = getFilteredDeviceTypes(
          system.system_type,
          deviceTypeMap,
          perms,
        );

        return (
          <div className={index !== filteredIntegrations.length - 1 ? 'mb-3' : ''} key={system.id}>
            <BaseAccordion
              bodyBackground="#232B33"
              header={
                <div>
                  <p className="fw-bold">{system.system_name}</p>
                  <p> {system.email}</p>
                </div>
              }
            >
              <div>
                <div>
                  <div className="d-flex justify-content-between">
                    <p>Type: {system.system_type}</p>
                    <EllipsisMenu
                      options={[
                        {
                          component: (
                            <>
                              {perms.testPermission(
                                [8, 9, 10, 11, 12, 13],
                                ['Create-EditIntegrations'],
                              ) && (
                                <Button
                                  className="w-100 px-5 text-center cursor-pointer text-white border border-1 border-black"
                                  onClick={() => openEditFormForSystemOrDevice('system', system)}
                                  variant="secondary"
                                >
                                  Edit
                                </Button>
                              )}
                            </>
                          ),
                        },
                        {
                          component: (
                            <>
                              {perms.testPermission(
                                [8, 9, 10, 11, 12, 13],
                                ['DeleteIntegrations'],
                              ) && (
                                <Button
                                  className="w-100 px-5 text-center cursor-pointer text-white border border-1 border-black"
                                  onClick={() => prepareSystemOrDeviceForDeletion(system)}
                                  variant="danger"
                                >
                                  Remove
                                </Button>
                              )}
                            </>
                          ),
                        },
                      ]}
                      open={systemRowIdEllipsisOpen[system.id]}
                      containerClassname="bg-transparent"
                      onOpen={() =>
                        setSystemRowIdEllipsisOpen({
                          ...systemRowIdEllipsisOpen,
                          [system.id]: true,
                        })
                      }
                      onClose={() =>
                        setSystemRowIdEllipsisOpen({
                          ...systemRowIdEllipsisOpen,
                          [system.id]: false,
                        })
                      }
                    />
                  </div>

                  <p>
                    License Info: {integrations?.length || 0}/{systemCount?.advanced_system || 0}{' '}
                    (System)
                  </p>

                  <div className="d-flex gap-2 flex-column flex-sm-row justify-content-between">
                    <p>Date Integrated: {new Date(system.date_integration).toLocaleString()}</p>

                    {system.status === 1 ? (
                      <p
                        className="bg-success rounded-3 py-1 text-white fs-6 fst-italic d-flex align-items-center justify-content-center"
                        style={{ width: '78.25px' }}
                      >
                        Online
                      </p>
                    ) : (
                      <p
                        className="bg-transparent border border-1 border-white rounded-3 py-1 text-white fs-6 fst-italic d-flex align-items-center justify-content-center"
                        style={{ width: '78.25px' }}
                      >
                        Offline
                      </p>
                    )}
                  </div>
                </div>

                {filteredDeviceTypes &&
                  Object.keys(filteredDeviceTypes).map((deviceType) => {
                    const filteredConnectedDevices = getfilteredConnectedDevices(
                      connectedDevices,
                      (device) => deviceSearchFilter(device, deviceDataCompat),
                      deviceType,
                    );

                    return filteredConnectedDevices.map((device, idx) => {
                      const hasDeletePermission = hasDevicePermissionFromDevice(
                        perms,
                        device,
                        'delete',
                      );
                      const hasCreateEditPermission = hasDevicePermissionFromDevice(
                        perms,
                        device,
                        'createEdit',
                      );

                      return (
                        <div
                          key={device.id}
                          className={`mt-3 py-3 border-1 border-top row align-items-center justify-content-sm-between justify-content-center g-3 ${
                            idx === filteredConnectedDevices.length - 1 && 'border-bottom'
                          }`}
                          style={{ borderColor: 'rgba(255, 255, 255, 0.35)' }}
                        >
                          <div className="col-12 col-sm-4 d-flex justify-content-center">
                            <img
                              src={getDynamicDeviceIcon(device)}
                              alt={device?.system_type}
                              style={{ width: '44px', height: '61px' }}
                            />
                          </div>

                          <div className="col-12 col-sm-4 d-flex flex-column align-items-center text-center">
                            <div>
                              <h2 className="fs-6 fw-bold mb-0">{`${device?.alias}`}</h2>
                              <p>Type: {deviceType.replace('_', ' ')}</p>
                              <p>
                                License Info:{' '}
                                {`${deviceIndexMap.get(device.id)}/${
                                  systemCount?.connected_devices
                                } (Device)`}
                              </p>
                              <p>
                                Date Integrated: {new Date(device?.date_created).toLocaleString()}
                              </p>
                            </div>
                          </div>

                          {(hasCreateEditPermission || hasDeletePermission) && (
                            <div className="col-12 col-sm-4 align-self-start d-flex justify-content-sm-end justify-content-center">
                              <EllipsisMenu
                                options={[
                                  hasCreateEditPermission && {
                                    component: (
                                      <Button
                                        className="w-100 px-5 text-center cursor-pointer text-white border border-1 border-black"
                                        variant="secondary"
                                        onClick={() => {
                                          openEditFormForSystemOrDevice('device', device);
                                          setDeviceRowIdEllipsisOpen({
                                            ...deviceRowIdEllipsisOpen,
                                            [device.id]: false,
                                          });
                                        }}
                                      >
                                        Edit
                                      </Button>
                                    ),
                                  },
                                  hasDeletePermission && {
                                    component: (
                                      <Button
                                        className="w-100 px-5 text-center cursor-pointer text-white border border-1 border-black"
                                        variant="danger"
                                        onClick={() => {
                                          prepareSystemOrDeviceForDeletion(device);
                                          setDeviceRowIdEllipsisOpen({
                                            ...deviceRowIdEllipsisOpen,
                                            [device.id]: false,
                                          });
                                        }}
                                      >
                                        Remove
                                      </Button>
                                    ),
                                  },
                                ].filter(Boolean)}
                                open={deviceRowIdEllipsisOpen[device.id]}
                                containerClassname="bg-transparent"
                                onOpen={() =>
                                  setDeviceRowIdEllipsisOpen({
                                    ...deviceRowIdEllipsisOpen,
                                    [device.id]: true,
                                  })
                                }
                                onClose={() =>
                                  setDeviceRowIdEllipsisOpen({
                                    ...deviceRowIdEllipsisOpen,
                                    [device.id]: false,
                                  })
                                }
                              />
                            </div>
                          )}
                        </div>
                      );
                    });
                  })}
              </div>
            </BaseAccordion>
          </div>
        );
      })}
    </>
  );
};

export default Accordion;
